const STORAGE_KEY = 'sireEnrollmentDrafts';
const LEGACY_KEY = 'sireEnrollmentFormValues';

export const EMPTY_ENROLLMENT_VALUES = {
  name: '',
  birthYear: '',
  regNum: '',
  sireSire: '',
  dam: '',
  damSire: '',
  fee: '',
  semenAvailability: [],
  country: '',
  contact: '',
  junior: false,
  limited: false,
  barrels: false,
  poles: false,
  firstCropBirthYear: '',
  breedingReport: {},
  breedingList: {},
  w9: {},
  ownerName: '',
  ownerEmail: '',
  ownerAddress1: '',
  ownerAddress3: '',
  ownerPhone: '',
  signed: false,
  signedAt: null,
  signedName: '',
  signedStatement: '',
  signedRulesVersion: '',
  stationName: '',
  stationAddress1: '',
  stationAddress3: '',
  stationEmail: '',
  stationPhone: '',
  stationWebsite: '',
  socialMedia: '',
  website: '',
  marketingParagraph: '',
  papers: {},
  headPicture: {},
  profilePicture: {},
  video: {},
};

const emptyStore = () => ({ drafts: {}, activeDraftId: null });

export function createDraftId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function readRawStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyStore();
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || typeof parsed.drafts !== 'object') {
      return emptyStore();
    }
    return {
      drafts: parsed.drafts || {},
      activeDraftId: parsed.activeDraftId || null,
    };
  } catch {
    return emptyStore();
  }
}

function writeStore(store) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

/** One-time migrate from the single-blob localStorage key. */
export function migrateLegacyDraft() {
  try {
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (!legacy) return readRawStore();

    const store = readRawStore();
    const hasDrafts = Object.keys(store.drafts).length > 0;
    if (!hasDrafts) {
      const values = JSON.parse(legacy);
      const draftId = createDraftId();
      store.drafts[draftId] = {
        values: { ...EMPTY_ENROLLMENT_VALUES, ...values },
        currentStep: 0,
        enrollmentId: null,
        updatedAt: new Date().toISOString(),
      };
      store.activeDraftId = draftId;
      writeStore(store);
    }
    localStorage.removeItem(LEGACY_KEY);
    return store;
  } catch {
    localStorage.removeItem(LEGACY_KEY);
    return readRawStore();
  }
}

export function loadStore() {
  return migrateLegacyDraft();
}

export function listDrafts() {
  const store = loadStore();
  return Object.entries(store.drafts)
    .map(([id, draft]) => ({
      id,
      name: (draft?.values?.name || '').trim() || 'Untitled',
      currentStep: draft?.currentStep ?? 0,
      enrollmentId: draft?.enrollmentId || null,
      updatedAt: draft?.updatedAt || null,
      values: draft?.values || EMPTY_ENROLLMENT_VALUES,
    }))
    .sort((a, b) => {
      const aTime = a.updatedAt ? Date.parse(a.updatedAt) : 0;
      const bTime = b.updatedAt ? Date.parse(b.updatedAt) : 0;
      return bTime - aTime;
    });
}

export function getDraft(draftId) {
  if (!draftId) return null;
  const store = loadStore();
  const draft = store.drafts[draftId];
  if (!draft) return null;
  return {
    id: draftId,
    values: { ...EMPTY_ENROLLMENT_VALUES, ...(draft.values || {}) },
    currentStep: draft.currentStep ?? 0,
    enrollmentId: draft.enrollmentId || null,
    updatedAt: draft.updatedAt || null,
  };
}

export function saveDraft(draftId, { values, currentStep, enrollmentId } = {}) {
  if (!draftId) return;
  const store = loadStore();
  const existing = store.drafts[draftId] || {
    values: EMPTY_ENROLLMENT_VALUES,
    currentStep: 0,
    enrollmentId: null,
  };
  store.drafts[draftId] = {
    values: values !== undefined ? values : existing.values,
    currentStep: currentStep !== undefined ? currentStep : existing.currentStep,
    enrollmentId:
      enrollmentId !== undefined ? enrollmentId : existing.enrollmentId,
    updatedAt: new Date().toISOString(),
  };
  store.activeDraftId = draftId;
  writeStore(store);
}

export function removeDraft(draftId) {
  if (!draftId) return;
  const store = loadStore();
  delete store.drafts[draftId];
  if (store.activeDraftId === draftId) {
    store.activeDraftId = null;
  }
  writeStore(store);
}

export function setActiveDraftId(draftId) {
  const store = loadStore();
  store.activeDraftId = draftId || null;
  writeStore(store);
}

export function getActiveDraftId() {
  return loadStore().activeDraftId;
}

export function createBlankDraft() {
  const draftId = createDraftId();
  saveDraft(draftId, {
    values: { ...EMPTY_ENROLLMENT_VALUES },
    currentStep: 0,
    enrollmentId: null,
  });
  return draftId;
}
